import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ListCourseDto } from '../general/dtos';
import { useApi } from './api-provider';
import { useAuth } from './auth-provider';
import { useBookmarkSelection } from '../general/bookmark-selection';

interface ProductsDataContextValue {
    courses: ListCourseDto[];
    selectedCourse: ListCourseDto | null;
    setSelectedCourse: (course: ListCourseDto | null) => void;
    loading: boolean;
    error: string;
    refresh: () => void;
}

const ProductsDataContext = createContext<ProductsDataContextValue | null>(null);

export function useProducts() {
    const context = useContext(ProductsDataContext);
    if (!context) throw new Error('useProducts must be used inside ProductsDataProvider');
    return context;
}

export function ProductsDataProvider({ children }: { children: React.ReactNode }) {
    const api = useApi();
    const auth = useAuth();
    const authenticated = auth.getIsAuthenticated();
    const authLoading = auth.getIsLoading();
    const [courses, setCourses] = useState<ListCourseDto[]>([]);
    const [selectedKey, selectKey] = useBookmarkSelection('course');
    const selectedCourse = courses.find(course => course.course_key === selectedKey) ?? null;
    const setSelectedCourse = useCallback((course: ListCourseDto | null) => selectKey(course?.course_key ?? null), [selectKey]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const linkError = !loading && !authLoading && !error && selectedKey && !selectedCourse
        ? 'The linked course is not available in your product list.' : '';
    const [revision, setRevision] = useState(0);
    const refresh = useCallback(() => setRevision(value => value + 1), []);

    useEffect(() => {
        if (!api || authLoading) return;
        let active = true;
        setLoading(true);
        setError('');
        setCourses([]);
        api.getProducts(authenticated).then(result => {
            if (!Array.isArray(result?.data)) throw new Error('Invalid course catalogue response.');
            if (active) setCourses(result.data);
        }).catch(() => {
            if (active) setError('Could not load available courses. Please try again later.');
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, [api, authenticated, authLoading, revision]);

    return (
        <ProductsDataContext.Provider value={{ courses, selectedCourse, setSelectedCourse, loading, error: error || linkError, refresh }}>
            {children}
        </ProductsDataContext.Provider>
    );
}
